import type { SupplyIntelEstimateParams } from "./config";

interface ListingEstimateInput {
  listPriceUsd: number;
  params: SupplyIntelEstimateParams;
  preOwned: boolean;
  /** Expected share of listed units sold within `sellThroughHorizonDays` (0–1). */
  sellThrough: number;
  sellThroughHorizonDays: number;
  windowDays: number;
}

const DAYS_PER_WEEK = 7;

/**
 * Share of listed units sold by `day` when `sellThrough` of them sell within
 * `sellThroughHorizonDays`. Sales follow `1 − e^(−t/τ)`, with τ set so
 * `shareOfSalesInFirst30Days` of eventual sales land by day 30. Assumes a
 * positive horizon and a sell-through the curve can reach (an eventual share
 * of at most 1).
 */
export function supplyIntelSoldShareByDay({
  day,
  params,
  sellThrough,
  sellThroughHorizonDays,
}: {
  day: number;
  params: Pick<SupplyIntelEstimateParams, "shareOfSalesInFirst30Days">;
  sellThrough: number;
  sellThroughHorizonDays: number;
}): number {
  const tau = -30 / Math.log(1 - params.shareOfSalesInFirst30Days);
  const soldByDay = (t: number) => 1 - Math.exp(-t / tau);
  return (sellThrough * soldByDay(day)) / soldByDay(sellThroughHorizonDays);
}

/** What liquidating one unit recovers once its value has decayed by `decayFactor`, after handling. */
function liquidationValueOnDay(
  listPriceUsd: number,
  params: SupplyIntelEstimateParams,
  decayFactor: number,
): number {
  return (
    params.liquidationShareOfList * listPriceUsd * decayFactor -
    params.liquidationHandlingUsd
  );
}

/** What liquidating one unit today recovers, after handling. */
export function liquidationValuePerUnit(
  listPriceUsd: number,
  params: SupplyIntelEstimateParams,
): number {
  return liquidationValueOnDay(listPriceUsd, params, 1);
}

/**
 * Expected value of listing one unit for `windowDays`, then liquidating it if
 * unsold, from the resale unit economics model. Sales follow
 * `supplyIntelSoldShareByDay`; price and liquidation value decay weekly;
 * unsold units pay holding cost each day; a returned sale refunds the buyer,
 * shipping included, and the unit is liquidated.
 */
function listingValuePerUnit({
  listPriceUsd,
  params,
  preOwned,
  sellThrough,
  sellThroughHorizonDays,
  windowDays,
}: ListingEstimateInput): number {
  const saleShareOfList = preOwned
    ? params.preOwnedSalePriceShareOfList
    : params.salePriceShareOfList;
  const decayFactor = (day: number) =>
    (1 - params.valueDecayPerWeek) ** (day / DAYS_PER_WEEK);

  let total = -params.listingCostUsd;
  let soldShareBefore = 0;
  for (let day = 1; day <= windowDays; day++) {
    const soldShare = supplyIntelSoldShareByDay({
      day,
      params,
      sellThrough,
      sellThroughHorizonDays,
    });
    const salePrice = saleShareOfList * listPriceUsd * decayFactor(day);
    const costPerSale =
      (salePrice + params.buyerShippingUsd) *
        (1 + params.salesTaxShare) *
        params.marketplaceFeeShare +
      params.perOrderFeeUsd +
      params.outboundHandlingUsd;
    const netPerReturn =
      liquidationValueOnDay(listPriceUsd, params, decayFactor(day)) -
      costPerSale -
      params.buyerShippingUsd -
      params.costPerReturnUsd;
    const netPerSale =
      (1 - params.returnRate) * (salePrice - costPerSale) +
      params.returnRate * netPerReturn;
    total +=
      (soldShare - soldShareBefore) * netPerSale -
      (1 - soldShareBefore) * params.holdingCostPerDayUsd;
    soldShareBefore = soldShare;
  }

  const lateLiquidation = liquidationValueOnDay(
    listPriceUsd,
    params,
    decayFactor(windowDays),
  );
  return total + (1 - soldShareBefore) * lateLiquidation;
}

/** Gain per unit from listing instead of liquidating today. Negative means liquidate. */
export function resaleGainPerUnit(input: ListingEstimateInput): number {
  return (
    listingValuePerUnit(input) -
    liquidationValuePerUnit(input.listPriceUsd, input.params)
  );
}
